import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { BarChart3, Star, Globe, ChefHat, Utensils, TrendingUp } from "lucide-react";
import { API } from "@/App";

const COLORS = ['#D4AF37', '#C9A227', '#B8960F', '#A68A00', '#9A7E00', '#8E7200', '#826600', '#765A00'];
const AXIS_TICK = { fill: '#888', fontSize: 10 };
const AXIS_TICK_SM = { fill: '#888', fontSize: 9 };
const GOLD_DOT = { fill: '#D4AF37', r: 4 };
const BRIGHT_DOT = { fill: '#FFD700', r: 4 };
const BAR_RADIUS = [0, 4, 4, 0];

const AnalyticsSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      console.warn("Analytics fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-8">
          <div className="flex justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!analytics || analytics.total_stats.total_visits === 0) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            {t('analytics.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-6">
          <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">{t('analytics.noData')}</p>
          <Button 
            onClick={() => navigate('/restaurants')}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {t('recommendations.exploreNow')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { monthly_visits, star_distribution, cuisine_breakdown, country_breakdown, total_stats } = analytics;

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded p-2 shadow-lg">
          <p className="text-sm font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={entry.name || entry.dataKey || index} className="text-xs" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="analytics-section">
      {/* Stats Overview */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
        <CardContent className="py-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold gradient-text">{total_stats.total_visits}</p>
              <p className="text-xs text-muted-foreground">{t('analytics.totalStats.visits')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold gradient-text">{total_stats.total_stars}</p>
              <p className="text-xs text-muted-foreground">{t('analytics.totalStats.stars')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold gradient-text">{total_stats.unique_restaurants}</p>
              <p className="text-xs text-muted-foreground">{t('analytics.totalStats.restaurants')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold gradient-text">{total_stats.unique_countries}</p>
              <p className="text-xs text-muted-foreground">{t('analytics.totalStats.countries')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold gradient-text">{total_stats.unique_cuisines}</p>
              <p className="text-xs text-muted-foreground">{t('analytics.totalStats.cuisines')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Visits Line Chart */}
        {monthly_visits.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                {t('analytics.monthlyVisits')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={monthly_visits}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis 
                    dataKey="month" 
                    tick={AXIS_TICK}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis tick={AXIS_TICK} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="visits" 
                    stroke="#D4AF37" 
                    strokeWidth={2}
                    dot={GOLD_DOT}
                    name={t('analytics.visits')}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="stars" 
                    stroke="#FFD700" 
                    strokeWidth={2}
                    dot={BRIGHT_DOT}
                    name={t('analytics.stars')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Star Distribution Pie Chart */}
        {star_distribution.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Star className="w-4 h-4 text-primary" />
                {t('analytics.starDistribution')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={star_distribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="label"
                  >
                    {star_distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    formatter={(value) => <span className="text-xs">{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Cuisine Breakdown Bar Chart */}
        {cuisine_breakdown.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <ChefHat className="w-4 h-4 text-primary" />
                {t('analytics.cuisineBreakdown')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={cuisine_breakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis type="number" tick={AXIS_TICK} />
                  <YAxis 
                    type="category" 
                    dataKey="cuisine" 
                    tick={AXIS_TICK_SM}
                    width={80}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="count" 
                    fill="#D4AF37" 
                    radius={BAR_RADIUS}
                    name={t('analytics.visits')}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Country Breakdown Bar Chart */}
        {country_breakdown.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Globe className="w-4 h-4 text-primary" />
                {t('analytics.countryBreakdown')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={country_breakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis type="number" tick={AXIS_TICK} />
                  <YAxis 
                    type="category" 
                    dataKey="country" 
                    tick={AXIS_TICK}
                    width={70}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="count" 
                    fill="#FFD700" 
                    radius={BAR_RADIUS}
                    name={t('analytics.visits')}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AnalyticsSection;
